import { CssBaseline, ThemeProvider } from '@mui/material';
import { LicenseInfo } from '@mui/x-data-grid-pro';
import { useUserContext, WithAuth, WithUser } from '@schooly/components/authentication';
import { ConfirmationDialogProvider } from '@schooly/components/confirmation-dialog';
import { WithDnd } from '@schooly/components/drag-and-drop';
import { PendingInviteModal } from '@schooly/components/invites';
import { WithNotifications } from '@schooly/components/notifications';
import { WithSentryUser } from '@schooly/sentry';
import { Loading, theme } from '@schooly/style';
import { getEnv } from '@schooly/utils/runtime-env';
import { FC, Suspense, lazy, useEffect } from 'react';

import CookieNotification from './components/common/CookieNotification';
import { FormbricksSurvey } from './components/common/FormbricksSurvey';
import MaintenanceModal from './components/common/MaintenanceModal';
import { TrialBannerLayout } from './components/common/Trial/TrialBannerLayout';
import { MUI_PRO_LICENSE_KEY } from './config';
import { WithCollapsiblePanel } from './context/collapsiblePanel/WithCollapsiblePanel';
import { LastRefreshTimeContextProvider } from './context/LastRefreshTimeContext';
import useMaintenance from './context/maintenance/useMaintenance';
import { WithRouter } from './context/router/WithRouter';
import { WithRouterState } from './context/router/WithRouterState';
import { WithSchool } from './context/SchoolContext';
import useAppLocation from './hooks/useAppLocation';
import usePrevious from './hooks/usePrevious';
import { DemoSchoolCtaBanner } from './pages/DemoSchool/DemoSchoolCtaBanner';
import MaintenancePage from './pages/Maintenance';
import { AppRoutes } from './routes/AppRoutes';

const SHOW_E2E_PANEL = getEnv('E2E_DEV') === 'true';
const E2E_PANEL_MODULE = './components/e2e-dev/E2eDevPanel';
const E2eDevPanel = SHOW_E2E_PANEL ? lazy(() => import(/* @vite-ignore */ E2E_PANEL_MODULE)) : null;

function App() {
  const { pathname, state: locationState } = useAppLocation();

  const prevLocationState = usePrevious(locationState);
  const { init: maintenanceInit, enabled: maintenanceEnabled } = useMaintenance();

  const init = maintenanceInit && !maintenanceEnabled;

  // Reset scroll on every page transition
  // biome-ignore lint/correctness/useExhaustiveDependencies: Legacy code
  useEffect(() => {
    if (!init) {
      return;
    }

    // Do not involve this logic if we open/close modals with URLs
    if (locationState?.backgroundLocation || prevLocationState?.backgroundLocation) {
      return;
    }

    window.scrollTo(0, 0);
  }, [init, pathname, locationState]); // eslint-disable-line react-hooks/exhaustive-deps

  // display loader while maintenance is checking
  if (!maintenanceInit && !maintenanceEnabled) {
    return <Loading />;
  }

  const content = <AppContent maintenanceEnabled={maintenanceEnabled} />;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <WithCollapsiblePanel>
        <WithDnd>
          <WithNotifications>
            <ConfirmationDialogProvider>
              <LastRefreshTimeContextProvider>
                {maintenanceEnabled ? content : <WithUser>{content}</WithUser>}
              </LastRefreshTimeContextProvider>
            </ConfirmationDialogProvider>
          </WithNotifications>
        </WithDnd>
      </WithCollapsiblePanel>
      {E2eDevPanel && (
        <Suspense fallback={null}>
          <E2eDevPanel />
        </Suspense>
      )}
    </ThemeProvider>
  );
}

const AppContent: FC<{
  maintenanceEnabled?: boolean;
}> = ({ maintenanceEnabled }) => {
  const { user, isFetching: isUserFetching } = useUserContext();

  useEffect(() => {
    LicenseInfo.setLicenseKey(MUI_PRO_LICENSE_KEY);
  }, []);

  if (user === undefined && !maintenanceEnabled) {
    return <Loading />;
  }

  const showPendingInviteModal = !isUserFetching && user;

  return (
    <WithAuth>
      <WithSentryUser>
        <WithSchool>
          <WithRouterState>
            <WithRouter>
              <TrialBannerLayout>
                {maintenanceEnabled ? <MaintenancePage /> : <AppRoutes />}
                {!maintenanceEnabled && <MaintenanceModal />}
                <CookieNotification />
                <DemoSchoolCtaBanner />
                <FormbricksSurvey />
                {showPendingInviteModal && <PendingInviteModal user={user} />}
              </TrialBannerLayout>
            </WithRouter>
          </WithRouterState>
        </WithSchool>
      </WithSentryUser>
    </WithAuth>
  );
};

export default App;
